import React from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline, CircleMarker, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';

type TrailPoint = {
  lat: number;
  lng: number;
  timestamp?: string;
};

interface MiniMapProps {
  lat: number;
  lng: number;
  placa: string;
  width?: number | string;
  height?: number | string;
  zoom?: number;
  interactive?: boolean;
  trail?: TrailPoint[];
  showTrailPoints?: boolean;
}

const sampleTrailPoints = (points: TrailPoint[], maxPoints = 12): TrailPoint[] => {
  if (points.length <= maxPoints) {
    return points;
  }

  const sampled: TrailPoint[] = [];
  const step = (points.length - 1) / (maxPoints - 1);

  for (let idx = 0; idx < maxPoints; idx += 1) {
    sampled.push(points[Math.round(idx * step)]);
  }

  return sampled;
};

const routesEqual = (a: Array<[number, number]>, b: Array<[number, number]>) => {
  if (a.length !== b.length) return false;
  for (let idx = 0; idx < a.length; idx += 1) {
    if (a[idx][0] !== b[idx][0] || a[idx][1] !== b[idx][1]) return false;
  }
  return true;
};

const MapAutoFit: React.FC<{ lat: number; lng: number; trail?: TrailPoint[] }> = ({ lat, lng, trail }) => {
  const map = useMap();

  React.useEffect(() => {
    const validTrail = (trail || []).filter(
      (p) => Number.isFinite(Number(p?.lat)) && Number.isFinite(Number(p?.lng))
    );

    if (validTrail.length >= 2) {
      const bounds = [...validTrail.map((p) => [p.lat, p.lng] as [number, number]), [lat, lng] as [number, number]];
      map.fitBounds(bounds, { padding: [24, 24] });
      return;
    }

    map.setView([lat, lng]);
  }, [map, lat, lng, trail]);

  return null;
};

const MiniMap: React.FC<MiniMapProps> = ({
  lat,
  lng,
  placa,
  width = 220,
  height = 180,
  zoom = 13,
  interactive = false,
  trail = [],
  showTrailPoints = false,
}) => {
  const [snappedRoute, setSnappedRoute] = React.useState<Array<[number, number]>>([]);
  const hasValidCenter = lat != null && lng != null;

  const validTrail = React.useMemo(
    () => trail.filter((p) => Number.isFinite(Number(p?.lat)) && Number.isFinite(Number(p?.lng))),
    [trail]
  );

  const trailCoords: [number, number][] = React.useMemo(
    () => validTrail.map((p) => [Number(p.lat), Number(p.lng)]),
    [validTrail]
  );

  // Usa uma chave estável por conteúdo para evitar reexecutar o efeito quando só a referência muda.
  const trailCoordsKey = React.useMemo(
    () => trailCoords.map((p) => `${p[0]},${p[1]}`).join('|'),
    [trailCoords]
  );

  React.useEffect(() => {
    let cancelled = false;

    const buildSnappedRoute = async () => {
      if (trailCoords.length < 2) {
        setSnappedRoute((prev) => (prev.length === 0 ? prev : []));
        return;
      }

      const sampled = sampleTrailPoints(validTrail);

      if (sampled.length < 2) {
        setSnappedRoute((prev) => (prev.length === 0 ? prev : []));
        return;
      }

      try {
        const coords = sampled.map((point) => `${point.lng},${point.lat}`).join(';');
        const response = await fetch(`https://router.project-osrm.org/route/v1/driving/${coords}?overview=full&geometries=geojson`);
        const data = await response.json();
        const geometry = data?.routes?.[0]?.geometry?.coordinates;

        if (!cancelled && Array.isArray(geometry) && geometry.length >= 2) {
          const nextRoute = geometry.map((coord: [number, number]) => [coord[1], coord[0]] as [number, number]);
          setSnappedRoute((prev) => (routesEqual(prev, nextRoute) ? prev : nextRoute));
          return;
        }
      } catch {
        // Fallback silencioso para a trilha bruta.
      }

      if (!cancelled) {
        setSnappedRoute((prev) => (prev.length === 0 ? prev : []));
      }
    };

    buildSnappedRoute();

    return () => {
      cancelled = true;
    };
  }, [trailCoords.length, trailCoordsKey, validTrail]);

  const displayRoute = snappedRoute.length >= 2 ? snappedRoute : trailCoords;

  if (!hasValidCenter) return null;

  return (
    <div style={{ width, height, borderRadius: 8, overflow: 'hidden', boxShadow: '0 2px 8px #0002' }}>
      <MapContainer
        center={[lat, lng]}
        zoom={zoom}
        style={{ width: '100%', height: '100%' }}
        scrollWheelZoom={interactive}
        dragging={interactive}
        doubleClickZoom={interactive}
        zoomControl={interactive}
      >
        <TileLayer
          attribution='&copy; OpenStreetMap contributors'
          url='https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
        />

        <MapAutoFit lat={lat} lng={lng} trail={trail} />

        {displayRoute.length >= 2 && (
          <Polyline positions={displayRoute} pathOptions={{ color: '#1d4ed8', weight: 4, opacity: 0.85 }} />
        )}

        {showTrailPoints && trailCoords.map((point, idx) => (
          <CircleMarker
            key={`${point[0]}-${point[1]}-${idx}`}
            center={point}
            radius={3}
            pathOptions={{ color: '#0f766e', fillColor: '#14b8a6', fillOpacity: 0.85, weight: 1 }}
          />
        ))}

        <Marker position={[lat, lng]}>
          <Popup>{placa}</Popup>
        </Marker>
      </MapContainer>
    </div>
  );
};

export default MiniMap;
